import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtAbsenceService} from '@app/services/gt-absence.service';
import {AbsenceEntry, AbsenceReason} from '@gt-api';
import * as momentNs from 'moment';
import {Subject} from 'rxjs';
import {map, switchMap, takeUntil, tap} from 'rxjs/operators';

const moment = momentNs;

class UICalendarMark {
  absenceId: number;
  type: string;
  cssClass: string;
  widthStyle: any;
  startPubQuarterId: number;
  endPubQuarterId: number;
  timeStr: string;
  reasonStr: string;
  reasonSubject: string;

  clone(): UICalendarMark {
    const tempMark: UICalendarMark = new UICalendarMark();
    tempMark.absenceId = this.absenceId;
    tempMark.type = this.type;
    tempMark.cssClass = this.cssClass;
    tempMark.widthStyle = this.widthStyle;
    tempMark.startPubQuarterId = this.startPubQuarterId;
    tempMark.endPubQuarterId = this.endPubQuarterId;
    tempMark.timeStr = this.timeStr;
    tempMark.reasonStr = this.reasonStr;
    tempMark.reasonSubject = this.reasonSubject;

    return tempMark;
  }
}

class UICalendar {
  indicatorPositionStyle: any;
  hourItemsStr: string[];
  calendarStartTime: momentNs.Moment;
  calendarEndTime: momentNs.Moment;
  calendarMarks: UICalendarMark[];
}

@Component({
  selector: 'gt-mini-calendar',
  templateUrl: './mini-calendar.component.html',
  styleUrls: ['./mini-calendar.component.scss']
})
export class MiniCalendarComponent implements OnInit, OnDestroy {
  @Input() oname: string;
  @Input() login: string;

  absenceReasons: AbsenceReason[];
  private readonly numHours = 8;
  private readonly numQuarters = 4;
  private readonly preDisplayMins = 90;
  private readonly perQuarterUIWidth = 3.125;
  private readonly tomorrowStartHour = 7; // 24 hours
  private readonly tomorrowStartMin = 30;
  private readonly timeStampFormat = 'HH:mm';
  private readonly dateFormat = 'DD.MM HH:mm';
  private dateWithYearFormat = 'DD.MM.YY HH:mm';
  private CSSClass = {
    BUSY: 'busy',
    BLOCKOUT: 'blocked-out'
  };

  ifTodayCal = true;
  todayCalendar: UICalendar;
  tomorrowCalendar: UICalendar;
  currentCalendar: UICalendar;
  toggleText = 'TOMORROW';
  private destroyed = new Subject<void>();

  constructor(private authenticationService: GtAuthenticationService, private absenceService: GtAbsenceService) {
    this.dateWithYearFormat = moment.localeData().longDateFormat('L');
  }

  ngOnInit() {
    this.setupCalendar();

    this.login = this.login || this.authenticationService.login;
    this.oname = this.oname || this.authenticationService.oname;

    this.absenceService
      .getAbsenceReasons(this.oname, this.login)
      .pipe(
        tap(reasons => (this.absenceReasons = reasons)),
        switchMap(_ => this.absenceService.getAbsences(this.oname, true)),
        map(absences => absences.filter(a => a.login === this.login)),
        tap(absences => this.setCalendarWithAbsences(absences)),
        takeUntil(this.destroyed)
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  private setCalendarWithAbsences(absences: AbsenceEntry[]): void {
    this.markCalendarWithAbsences(absences, this.todayCalendar);
    this.markCalendarWithAbsences(absences, this.tomorrowCalendar);
  }

  private setupCalendar() {
    this.todayCalendar = new UICalendar();
    this.tomorrowCalendar = new UICalendar();
    this.currentCalendar = this.todayCalendar;
    this.todayCalendar.hourItemsStr = [];
    this.tomorrowCalendar.hourItemsStr = [];

    const currentTime: momentNs.Moment = moment();
    this.todayCalendar.calendarStartTime = currentTime.clone().startOf('hour').subtract(this.preDisplayMins, 'minutes');
    this.tomorrowCalendar.calendarStartTime = moment({hour: this.tomorrowStartHour, minute: this.tomorrowStartMin}).add(1, 'days');
    this.todayCalendar.calendarEndTime = this.todayCalendar.calendarStartTime.clone().add(this.numHours, 'hours');
    this.tomorrowCalendar.calendarEndTime = this.tomorrowCalendar.calendarStartTime.clone().add(this.numHours, 'hours');

    for (let i = 0; i < this.numHours; i++) {
      const tempHour: momentNs.Moment = this.todayCalendar.calendarStartTime.clone().add(30, 'minutes').add(i, 'hours');
      this.todayCalendar.hourItemsStr.push(tempHour.format(this.timeStampFormat));

      const tomorrowHour: momentNs.Moment = this.tomorrowCalendar.calendarStartTime.clone().add(30, 'minutes').add(i, 'hours');
      this.tomorrowCalendar.hourItemsStr.push(tomorrowHour.format(this.timeStampFormat));
    }

    this.todayCalendar.calendarMarks = [];
    this.tomorrowCalendar.calendarMarks = [];

    this.setIndicator();
  }

  setIndicator() {
    const diffMins: number = moment().diff(this.todayCalendar.calendarStartTime, 'minutes');
    this.todayCalendar.indicatorPositionStyle = {left: (diffMins / (this.numHours * 60)) * 100 + '%'};
  }

  markCalendarWithAbsences(absences: AbsenceEntry[], calendar: UICalendar) {
    calendar.calendarMarks = [];

    absences.sort((a, b) => a.id - b.id);
    let zIndex = 4;

    absences.forEach((abs: AbsenceEntry) => {
      if (abs.startTime) {
        const tempUIMark: UICalendarMark = this.generateAbsUIMark(
          abs.startTime as any,
          abs.endTime ? (abs.endTime as any) : null,
          abs.reasonId,
          abs.id,
          abs.appointmentInfo && abs.appointmentInfo.subject ? abs.appointmentInfo.subject : '',
          calendar.calendarStartTime,
          calendar.calendarEndTime,
          zIndex
        );

        if (tempUIMark) {
          zIndex++;
          calendar.calendarMarks.push(tempUIMark);
        }
      }
    });

    // sort UIMarks by increased startTime
    calendar.calendarMarks.sort((a, b) => a.startPubQuarterId - b.startPubQuarterId);
    this.createBlanketMark(calendar);
  }

  // insert blanket UICalendarMark in calendarMarks
  private createBlanketMark(calendar: UICalendar) {
    const originalCalendarMarksLength = calendar.calendarMarks.length;
    for (let key = 0; key < originalCalendarMarksLength; key++) {
      const mark: UICalendarMark = calendar.calendarMarks[key];

      if (key === 0 && mark.startPubQuarterId !== 0) {
        calendar.calendarMarks.push(this.generateBlanketUIMark(0, mark.startPubQuarterId - 1));
      }

      if (key + 1 !== originalCalendarMarksLength) {
        // not the last absence
        const nextMark: UICalendarMark = calendar.calendarMarks[key + 1];
        if (nextMark.startPubQuarterId - mark.endPubQuarterId > 1) {
          // gap between absence mark insert blanket mark
          calendar.calendarMarks.push(this.generateBlanketUIMark(mark.endPubQuarterId + 1, nextMark.startPubQuarterId - 1));
        } else if (nextMark.startPubQuarterId <= mark.endPubQuarterId && nextMark.endPubQuarterId >= mark.endPubQuarterId) {
          if (mark.absenceId > nextMark.absenceId) {
            nextMark.startPubQuarterId = mark.endPubQuarterId + 1;
            this.updateWidthStyle(nextMark);
          } else {
            mark.endPubQuarterId = nextMark.startPubQuarterId - 1;
            this.updateWidthStyle(mark);
          }
        } else if (nextMark.startPubQuarterId <= mark.endPubQuarterId && nextMark.endPubQuarterId < mark.endPubQuarterId) {
          if (mark.absenceId > nextMark.absenceId) {
            nextMark.endPubQuarterId = mark.endPubQuarterId;
            this.updateWidthStyle(nextMark);
          } else {
            // next UIMark split current UIMark into two marks, replace the next UIMark with the later part of current UIMark and
            // push original next UIMark into calendarMarks
            const originMark: UICalendarMark = mark.clone();
            const originNextMark: UICalendarMark = nextMark.clone();

            // first part of current UIMark
            mark.endPubQuarterId = originNextMark.startPubQuarterId - 1;
            this.updateWidthStyle(mark);

            // second part of current UIMark as new next UIMark
            calendar.calendarMarks[key + 1] = originMark;
            calendar.calendarMarks[key + 1].startPubQuarterId = originNextMark.endPubQuarterId + 1;
            this.updateWidthStyle(calendar.calendarMarks[key + 1]);

            // push original NextMark to the end of calendarMarks
            calendar.calendarMarks.push(originNextMark);
          }
        }
      }
    }

    // sort UIMarks after inserting blanket UICalendarMark
    calendar.calendarMarks.sort((a, b) => a.startPubQuarterId - b.startPubQuarterId);
  }

  private generateBlanketUIMark(startPubQuarterId: number, endPubQuarterId: number): UICalendarMark {
    const blanketMark: UICalendarMark = new UICalendarMark();
    blanketMark.type = 'available';
    blanketMark.cssClass = '';
    blanketMark.startPubQuarterId = startPubQuarterId;
    blanketMark.endPubQuarterId = endPubQuarterId;
    blanketMark.widthStyle = {width: (endPubQuarterId - startPubQuarterId + 1) * this.perQuarterUIWidth + '%'};

    return blanketMark;
  }

  private generateAbsUIMark(
    absStartTimeStamp: number,
    absEndTimeStamp: number,
    absReasonId: number,
    absID: number,
    absSubject: string,
    calendarStartTime: momentNs.Moment,
    calendarEndTime: momentNs.Moment,
    zIndex: number
  ): UICalendarMark {
    const tempStartMoment = moment(absStartTimeStamp);
    let tempEndMoment;
    if (absEndTimeStamp) {
      tempEndMoment = moment(absEndTimeStamp);
    } else {
      tempEndMoment = calendarEndTime.clone();
    }

    if (tempStartMoment > calendarEndTime) {
      return null;
    }
    if (tempEndMoment < calendarStartTime) {
      return null;
    }

    const tempUICalendarMark: UICalendarMark = new UICalendarMark();
    tempUICalendarMark.absenceId = absID;
    tempUICalendarMark.startPubQuarterId = this.getQuarterId(calendarStartTime, calendarEndTime, tempStartMoment);
    tempUICalendarMark.endPubQuarterId = this.getQuarterId(calendarStartTime, calendarEndTime, tempEndMoment) - 1;
    tempUICalendarMark.widthStyle = {
      'z-index': zIndex
    };
    this.updateWidthStyle(tempUICalendarMark);

    if (
      (absEndTimeStamp && moment(absEndTimeStamp).get('year')) !== calendarEndTime.get('year') ||
      moment(absStartTimeStamp).get('year') !== calendarStartTime.get('year')
    ) {
      tempUICalendarMark.timeStr = absEndTimeStamp
        ? moment(absStartTimeStamp).format(this.dateWithYearFormat) + ' - ' + moment(absEndTimeStamp).format(this.dateWithYearFormat)
        : moment(absStartTimeStamp).format(this.dateWithYearFormat);
    } else if (
      moment(absStartTimeStamp).endOf('day') < calendarStartTime.clone().startOf('day') ||
      (absEndTimeStamp && moment(absEndTimeStamp).startOf('day')) > calendarEndTime.clone().endOf('day')
    ) {
      tempUICalendarMark.timeStr = absEndTimeStamp
        ? moment(absStartTimeStamp).format(this.dateFormat) + ' - ' + moment(absEndTimeStamp).format(this.dateFormat)
        : moment(absStartTimeStamp).format(this.dateFormat);
    } else {
      tempUICalendarMark.timeStr = absEndTimeStamp
        ? moment(absStartTimeStamp).format(this.dateFormat) + ' - ' + moment(absEndTimeStamp).format(this.dateFormat)
        : moment(absStartTimeStamp).format(this.dateFormat);
    }

    const tempReason = this.absenceReasons.find(r => r.id === absReasonId);

    tempUICalendarMark.reasonStr = tempReason.value;
    tempUICalendarMark.reasonSubject = absSubject;

    tempUICalendarMark.type = tempReason.value;
    tempUICalendarMark.cssClass = this.getCSSClassByType(tempUICalendarMark.type);

    return tempUICalendarMark;
  }

  private getQuarterId(startTime: momentNs.Moment, endTime: momentNs.Moment, time: momentNs.Moment) {
    if (time.isBefore(startTime)) {
      return 0;
    }

    if (time.isAfter(endTime)) {
      time = endTime;
    }
    return Math.round(time.diff(startTime, 'minute') / 15);
  }

  private updateWidthStyle(mark: UICalendarMark): void {
    mark.widthStyle = {
      ...mark.widthStyle,
      width:
        mark && mark.endPubQuarterId >= mark.startPubQuarterId
          ? `${(mark.endPubQuarterId - mark.startPubQuarterId + 1) * this.perQuarterUIWidth}%`
          : '0%',
      left: `${mark.startPubQuarterId * this.perQuarterUIWidth}%`
    };
  }

  private getCSSClassByType(type: string): string {
    let cssClass;

    switch (type) {
      // case this.absenceService.ReasonTypes.MEETING:
      //   cssClass = this.CSSClass.BLOCKOUT;
      //   break;
      // case this.absenceService.ReasonTypes.LUNCH:
      //   cssClass = this.CSSClass.BLOCKOUT;
      //   break;
      // case this.absenceService.ReasonTypes.LEFTDAY:
      //   cssClass = this.CSSClass.BLOCKOUT;
      //   break;
      // case this.absenceService.ReasonTypes.BUSY:
      //   cssClass = this.CSSClass.BUSY;
      //   break;
      // case this.absenceService.ReasonTypes.OUTOFFICE:
      //   cssClass = this.CSSClass.BLOCKOUT;
      //   break;
      // case this.absenceService.ReasonTypes.VACATION:
      //   cssClass = this.CSSClass.BLOCKOUT;
      //   break;
      default:
        cssClass = this.CSSClass.BLOCKOUT;
    }

    return cssClass;
  }
}
